import React from "react";
import FolderOpenRoundedIcon from "@material-ui/icons/FolderOpenRounded";
import FadeInSection from "./FadeInSection";
import SectionHeading from "./SectionHeading";
import ExternalLinks from "./ExternalLinks";
import Carousel from "./Carousel";

const SPOTLIGHT = [
    {
        key: "farmcart",
        title: "FarmCart",
        desc: "An e-commerce platform that streamlines buying and selling farm products — authentication, cart, secure checkout and order management, on a scalable backend with a responsive frontend.",
        techStack: "React · Node.js · MongoDB · Express",
        link: "https://github.com/GayashanHansaja/FarmCartf",
        image: "/assets/farmcart.jpg",
    },
    {
        key: "beaver",
        title: "Beaver",
        desc: "A social app inspired by Instagram: authentication, post creation with image uploads, likes, comments and profiles. Supabase handles auth, database and storage.",
        techStack: "React Native · Expo · Supabase",
        link: "https://github.com/GayashanHansaja/Terra",
        image: "/assets/threads.jpg",
    },
    {
        key: "terra",
        title: "Terra",
        desc: "A web app for exploring countries and their details — search, browse and dig into related information, on a layout that works across devices.",
        techStack: "React · Tailwind CSS",
        link: "https://github.com/GayashanHansaja/Terra",
        image: "/assets/terra.jpg",
    },
    {
        key: "portfolio",
        title: "Portfolio",
        desc: "This site. A personal portfolio bringing together my work, experience and writing under one minimal, dark interface.",
        techStack: "React · Tailwind CSS",
        link: "https://github.com/GayashanHansaja/Hansa",
        image: "/assets/Portfolio.png",
    },
];

const PROJECTS = [
    {
        title: "Quizzer",
        desc: "A backend for a quiz application — quiz creation, taking, management and result calculation.",
        techStack: "Spring Boot · PostgreSQL · Java",
        link: "https://github.com/GayashanHansaja/quizzer",
    },
    {
        title: "CraveDrop",
        desc: "A microservice application for food delivery covering restaurant management, order processing and delivery.",
        techStack: "React · Node.js · MongoDB · Docker · Kubernetes",
        link: "https://github.com/GayashanHansaja/CraveDrop",
    },
    {
        title: "Fine-Track",
        desc: "Expense and income tracking — add, edit and delete transactions, review financial status and track money goals.",
        techStack: "Express.js · Node.js · MongoDB",
        link: "https://github.com/GayashanHansaja/project-GayashanHansaja",
    },
    {
        title: "Weather App",
        desc: "Current conditions and forecasts for any location, pulled from a weather API into a clean mobile interface.",
        techStack: "React Native · Expo",
        link: "https://github.com/GayashanHansaja/ReactNativeDemo-weather-App",
    },
    {
        title: "Calculator",
        desc: "A simple cross-platform calculator built to explore Flutter alongside a C++ core.",
        techStack: "Flutter · C++",
        link: "https://github.com/GayashanHansaja/project-GayashanHansaja",
    },
];

const Slide = project => (
    <>
        <img
            src={project.image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-canvas-deep via-canvas-deep/85 to-canvas-deep/30"
        />
        <div className="relative flex h-full flex-col justify-end p-6 pb-14 sm:p-10 sm:pb-12 lg:p-12">
            <p className="eyebrow mb-2 sm:mb-3">Spotlight</p>
            <h3 className="text-2xl font-semibold text-ink sm:text-3xl">
                {project.title}
            </h3>
            {/* Clamped on phones so the longest blurb can't push the caption
                out of the frame; shown in full once there's room. */}
            <p className="mt-3 line-clamp-4 max-w-prose text-[15px] leading-relaxed text-ink-dim sm:line-clamp-none">
                {project.desc}
            </p>
            <p className="mt-3 font-mono text-[11px] leading-relaxed tracking-[0.08em] text-accent sm:mt-4">
                {project.techStack}
            </p>
            <div className="mt-3 sm:mt-5">
                <ExternalLinks
                    githubLink={project.link}
                    openLink={project.open}
                    label={project.title}
                />
            </div>
        </div>
    </>
);

const Projects = () => (
    <section id="projects" className="section">
        <div className="shell">
            <FadeInSection>
                <SectionHeading index="03" title="projects" />
            </FadeInSection>

            <FadeInSection>
                <Carousel items={SPOTLIGHT} renderItem={Slide} />
            </FadeInSection>

            <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 sm:mt-14">
                {PROJECTS.map((project, i) => (
                    <li key={project.title} className="h-full">
                        <FadeInSection
                            delay={`${(i % 3) * 90}ms`}
                            className="h-full"
                        >
                            <div className="card group p-6">
                                <div className="mb-5 flex items-start justify-between">
                                    <FolderOpenRoundedIcon
                                        style={{ fontSize: 26 }}
                                        className="text-accent transition-transform duration-300 ease-soft group-hover:-translate-y-0.5"
                                    />
                                    <ExternalLinks
                                        githubLink={project.link}
                                        openLink={project.open}
                                        label={project.title}
                                    />
                                </div>

                                <h3 className="text-lg font-semibold text-ink">
                                    {project.title}
                                </h3>
                                <p className="mt-2.5 flex-1 text-[14px] leading-relaxed text-ink-faint">
                                    {project.desc}
                                </p>
                                <p className="mt-5 border-t border-line pt-4 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-faint">
                                    {project.techStack}
                                </p>
                            </div>
                        </FadeInSection>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

export default Projects;
